import {isCopilot} from '@github-ui/assignees/copilot-user'
import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ownerPath, teamPath} from '@github-ui/paths'
import {Link as PrimerLink} from '@primer/react'
import type {Reviewer} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'
import styles from './Reviewers.module.css'

type IdentityOptions = {
  withAvatar?: boolean
  withLeadingAt?: boolean
  // Render as an inline text link (honors the link-underline user preference and allows the name to wrap).
  // Use in running-text contexts where there is no leading avatar.
  inline?: boolean
}

const COPILOT_DISPLAY_NAME = 'Copilot'

export function ReviewerIdentity({reviewer, ...options}: {reviewer: Reviewer} & IdentityOptions) {
  return reviewer.reviewerType === 'Team' ? (
    <TeamIdentity reviewer={reviewer} {...options} />
  ) : (
    <UserIdentity reviewer={reviewer} {...options} />
  )
}

function UserIdentity({
  reviewer,
  withAvatar = true,
  withLeadingAt = false,
  inline = false,
}: {reviewer: Reviewer} & IdentityOptions) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const copilot = isCopilot(reviewer.displayLogin)
  const copilotSlug = reviewer.slug || reviewer.displayLogin

  const hovercardAttrs = copilot
    ? getHovercardAttributesForType('copilot', {botSlug: copilotSlug})
    : getHovercardAttributesForType('user', {login: reviewer.displayLogin})

  return (
    <IdentityLink
      href={copilot ? `/apps/${copilotSlug}` : ownerPath({owner: reviewer.displayLogin})}
      avatarUrl={withAvatar ? reviewer.avatarUrl : undefined}
      copilot={copilot}
      displayName={copilot ? COPILOT_DISPLAY_NAME : reviewer.displayLogin}
      withLeadingAt={withLeadingAt}
      inline={inline}
      hovercardAttrs={hovercardAttrs}
    />
  )
}

function TeamIdentity({
  reviewer,
  withAvatar = true,
  withLeadingAt = false,
  inline = false,
}: {reviewer: Reviewer} & IdentityOptions) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  // Team `displayLogin` values are prefixed with the org (e.g. `org/team`), but the unprefixed slug (e.g. `team`) is
  // what hovercards and team links expect. Fall back to stripping the prefix when `name` is empty.
  const teamSlug = reviewer.name || stripOrgPrefix(reviewer.displayLogin)

  const hovercardAttrs = getHovercardAttributesForType('team', {
    owner: reviewer.organizationLogin ?? '',
    team: teamSlug,
  })

  const href = reviewer.organizationLogin
    ? teamPath({owner: reviewer.organizationLogin, team: teamSlug})
    : ownerPath({owner: reviewer.displayLogin})

  // Show the shortened slug (the hovercard already surfaces the org name), except in mention contexts
  // (`withLeadingAt`) where the full `@org/team` must be kept so the mention stays valid.
  const displayName = withLeadingAt ? reviewer.displayLogin : teamSlug

  return (
    <IdentityLink
      href={href}
      avatarUrl={withAvatar ? reviewer.avatarUrl : undefined}
      square
      displayName={displayName}
      withLeadingAt={withLeadingAt}
      inline={inline}
      hovercardAttrs={hovercardAttrs}
    />
  )
}

function IdentityLink({
  href,
  avatarUrl,
  square = false,
  copilot = false,
  displayName,
  withLeadingAt,
  inline = false,
  hovercardAttrs,
}: {
  href: string
  avatarUrl?: string
  square?: boolean
  copilot?: boolean
  displayName: string
  withLeadingAt: boolean
  inline?: boolean
  hovercardAttrs: ReturnType<ReturnType<typeof useGetHovercardAttributesForType>>
}) {
  return (
    <PrimerLink
      href={href}
      muted
      inline={inline}
      className={inline ? styles.inlineReviewerIdentity : styles.reviewerIdentity}
      {...hovercardAttrs}
    >
      {avatarUrl !== undefined && (
        <span className={styles.reviewerAvatar}>
          {copilot ? <CopilotAvatar isDecorative /> : <GitHubAvatar src={avatarUrl} size={20} square={square} />}
        </span>
      )}
      <span className={inline ? styles.inlineReviewerName : styles.reviewerName}>
        {withLeadingAt && <span>@</span>}
        {displayName}
      </span>
    </PrimerLink>
  )
}

// Team `displayLogin` values are prefixed with the org (e.g. `org/team`); strip the prefix to get the team slug.
function stripOrgPrefix(displayLogin: string): string {
  const slashIndex = displayLogin.lastIndexOf('/')
  return slashIndex === -1 ? displayLogin : displayLogin.slice(slashIndex + 1)
}
